import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";

import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { FileSelectEvent, FileUpload, FileUploadModule } from "@openng/optimus-ui/fileupload";
import { TextareaModule } from "@openng/optimus-ui/textarea";
import { MessageService } from "@openng/optimus-ui/api";
import { SelectModule } from "@openng/optimus-ui/select";
import * as L from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import { NgStyle } from "@angular/common";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { concatMap, first, Observable, of } from "rxjs";
import { MediaService } from "../../../../../services/api/media/media.service";
import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { environment } from "../../../../../../environments/environment";
import { COUNTRY_OPTIONS } from "../../../../../constants/countryOptions";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { CacheService } from "../../../../../services/cache/cache.service";
import { HasPermissionDirective } from "../../../../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../../../../services/api/permission/permission.service";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { Building, BuildingDTO } from "../../../../../models/api/asset/building";
import { Media } from "../../../../../models/api/media";
import { EMAIL_PATTERN, IMAGE_PATTERN, PDF_PATTERN } from "../../../../../constants/regExp";
import { SupportedLang } from "../../../../../models/ui/supportedLangs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { createTimeoutSignal } from "../../../../../shared/utils/timeout.utils";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../../../../shared/components/timeout/timeout.component";
import { CommonUtilsService } from "../../../../../services/utils/common-utils/common-utils.service";

@Component({
  standalone: true,
  selector: "app-building",
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    InputNumberModule,
    FileUploadModule,
    TextareaModule,
    SelectModule,
    NgStyle,
    FormsModule,
    HasPermissionDirective,
    ProgressSpinnerModule,
    TimeoutComponent,
  ],
  templateUrl: "./building.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./building.component.scss",
})
export class BuildingComponent implements OnInit, AfterViewInit, OnDestroy {
  private config = inject(DynamicDialogConfig);

  public building: Building = this.config.data.asset;

  public form = new FormGroup({
    label: new FormControl(this.building.label, {
      validators: [Validators.required],
      nonNullable: true,
    }),
    email: new FormControl(this.building.email, [Validators.pattern(EMAIL_PATTERN)]),
    phone: new FormControl(this.building.phone),
    notes: new FormControl(this.building.notes),
    length: new FormControl(this.building.length),
    width: new FormControl(this.building.width),
    height: new FormControl(this.building.height),
    surface: new FormControl(this.building.surface),
    volume: new FormControl(this.building.volume),
    address: new FormGroup({
      street: new FormControl(this.building.address?.street),
      number: new FormControl(this.building.address?.number),
      zipCode: new FormControl(this.building.address?.zipCode),
      city: new FormControl(this.building.address?.city),
      country: new FormControl(this.building.address?.country),
    }),
    techEmployee: new FormGroup({
      name: new FormControl(this.building.techEmployee?.name),
      email: new FormControl(this.building.techEmployee?.email),
      phone: new FormControl(this.building.techEmployee?.phone),
    }),
    ecoEmployee: new FormGroup({
      name: new FormControl(this.building.ecoEmployee?.name),
      email: new FormControl(this.building.ecoEmployee?.email),
      phone: new FormControl(this.building.ecoEmployee?.phone),
    }),
    totalGateSurface: new FormControl(this.building.totalGateSurface),
    energyCost: new FormControl(this.building.energyCost),
    totalGates: new FormControl(this.building.totalGates),
    climateType: new FormControl(this.building.climateType, Validators.required),
    position: new FormGroup({
      latitude: new FormControl(this.building.position?.latitude),
      longitude: new FormControl(this.building.position?.longitude),
    }),
  });
  private ref = inject(DynamicDialogRef);
  public translate = inject(TranslateService);
  private message = inject(MessageService);
  public permissions = inject(PermissionService);
  public commonUtils = inject(CommonUtilsService);

  private map!: L.Map;
  private marker!: L.Marker;
  private icon!: L.Icon;
  private tileLayer!: L.TileLayer;
  public countries: DropdownOption[] = COUNTRY_OPTIONS;

  public climateTypeOptions: DropdownOption[] = [
    {
      label: this.translate.instant("gate_administration.building_form.none"),
      value: "none",
    },
    {
      label: this.translate.instant("gate_administration.building_form.cooling"),
      value: "cooling",
    },
    {
      label: this.translate.instant("gate_administration.building_form.heating"),
      value: "heating",
    },
  ];
  private actionType!: "add" | "edit";
  private mediaFile!: Media;

  public floorMapSource!: string;
  public requestStarted = signal(false);
  public timeoutRequest = createTimeoutSignal(this.requestStarted);
  public point: WritableSignal<{ x: number; y: number }> = signal({
    x: 0,
    y: 0,
  });

  private imgWidth = signal(0);
  private imgHeight = signal(0);
  private cache = inject(CacheService);

  public volume!: number;
  public surface!: number;

  private assetService = inject(AssetService);
  private mediaService = inject(MediaService);

  public selectedFloorMap!: File | null;

  public getPointCoordinates = computed(() => {
    if (this.point() !== null) {
      return {
        "top.px": this.point().y * this.imgHeight(),
        "left.px": this.point().x * this.imgWidth(),
      };
    } else {
      return {};
    }
  });

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: (langChange) => {
        const newLang = langChange.lang as SupportedLang;

        this.changeTiles(newLang);
      },
    });
  }

  public ngOnInit() {
    this.building = this.config.data.asset;
    if (this.config.data.asset) {
      this.calculateSizes();
      this.checkMedia();
      if (this.building.position && this.building.position.latitude && this.building.position.longitude) {
        this.point.set({
          x: this.building.position.latitude,
          y: this.building.position.longitude,
        });
      }
      this.extractFloorMapSource(this.building);
    }
    if (this.config.data.actionType) {
      this.actionType = this.config.data.actionType;
    }
  }

  public ngAfterViewInit(): void {
    if (!this.building.location?.locationFloorMapUrl) {
      this.initMap();
    }
  }

  public ngOnDestroy(): void {
    this.map?.remove();
  }

  /**
   * Extracts the floor map source from the building's location.
   * @param building The building whose location to extract the floor map source from.
   */
  private extractFloorMapSource(building: Building) {
    if (building.location?.locationFloorMapUrl) {
      this.floorMapSource = building.location.locationFloorMapUrl;
      const fileType = this.getFileType(building.location.locationFloorMapUrl);
      switch (fileType) {
        case "image":
          this.floorMapSource = building.location.locationFloorMapUrl;
          break;
        case "pdf":
          this.loadPdfAsImage(building.location.locationFloorMapUrl).then(
            (image) => (this.floorMapSource = image ?? "")
          );
          break;
        default:
          break;
      }
    }
  }

  /**
   * Checks the file type of the given URL.
   * @param url The URL of the file to check.
   * @returns "pdf" if the URL is a PDF, "image" if it is an image, and "other" otherwise.
   */
  private getFileType(url: string): string {
    if (PDF_PATTERN.test(url)) {
      return "pdf";
    } else if (IMAGE_PATTERN.test(url)) {
      return "image";
    } else {
      return "other";
    }
  }

  /**
   * Loads a PDF as an image.
   * @param pdfUrl The URL of the PDF to load.
   * @returns A data URL of the PDF as an image, or null if an error occurs.
   */
  private async loadPdfAsImage(pdfUrl: string) {
    try {
      GlobalWorkerOptions.workerSrc = "assets/pdf.worker.min.mjs";

      const loadingTask = getDocument({ url: pdfUrl });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1); // Get the first page

      const scale = 2; // Adjust for better resolution
      const viewport = page.getViewport({ scale });

      // Create a canvas element
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d")!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvas: canvas,
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Convert canvas to image URL
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error loading PDF:", error);
      return;
    }
  }

  /**
   * Called when the image is loaded.
   * @param event The event that triggered the image load.
   */
  public onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    this.imgWidth.set(img.width);
    this.imgHeight.set(img.height);
  }

  public onImageClick(event: MouseEvent) {
    const rect = (event.target as HTMLElement).getBoundingClientRect();

    const imageWidth = rect.width;
    const imageHeight = rect.height;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const xRelative = x / imageWidth;
    const yRelative = y / imageHeight;

    this.point.set({ x: xRelative, y: yRelative });
    this.form?.patchValue({
      position: { latitude: xRelative, longitude: yRelative },
    });
  }

  /**
   * Called when user changes the width, height, or length of a building.
   */
  public sizeChange() {
    this.calculateSizes();
  }

  /**
   * Calculates and updates the form's volume and surface fields based on width, height, and length.
   * Volume is calculated as width * height * length, and surface as width * length.
   * Rounds results to two decimal places. Sets fields to null if required values are missing.
   */
  private calculateSizes() {
    const width = this.form?.get("width")?.value;
    const height = this.form?.get("height")?.value;
    const length = this.form?.get("length")?.value;

    if (width && height && length) {
      const volume = Math.round(width * height * length * 100) / 100;
      this.form?.patchValue({ volume: volume });
    } else {
      this.form?.patchValue({ volume: null });
    }

    if (width && length) {
      const surface = Math.round(width * length * 100) / 100;
      this.form?.patchValue({ surface: surface });
    } else {
      this.form?.patchValue({ surface: null });
    }
  }

  /**
   * Save the building form.
   * If the form is valid, creates or edits the building according to the action type.
   * If the form is invalid, shows an error message.
   */
  public save() {
    const buildingFormDTO = this.createBuildingDTO();

    if (buildingFormDTO && this.form?.valid) {
      if (!this.requestStarted()) {
        this.building.properties = buildingFormDTO;
        this.requestStarted.set(true);
        if (this.actionType === "add") {
          this.addBuilding();
        } else if (this.actionType === "edit") {
          this.editBuilding();
        }
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_form"),
        life: 4500,
      });
    }
  }

  /**
   * Creates a BuildingDTO based on the current form values.
   * @returns a BuildingDTO or null on invalid form
   */
  private createBuildingDTO(): BuildingDTO | null {
    const buildingFormValues = this.form?.value;
    if (buildingFormValues && buildingFormValues.label) {
      const building: BuildingDTO = {
        label: buildingFormValues.label,
        email: buildingFormValues.email,
        phone: buildingFormValues.phone,
        length: buildingFormValues.length,
        notes: buildingFormValues.notes,
        height: buildingFormValues.height,
        width: buildingFormValues.width,
        address: buildingFormValues.address,
        techEmployee: buildingFormValues.techEmployee,
        ecoEmployee: buildingFormValues.ecoEmployee,
        totalGateSurface: buildingFormValues.totalGateSurface,
        energyCost: buildingFormValues.energyCost,
        totalGates: buildingFormValues.totalGates,
        climateType: buildingFormValues.climateType,
        position: this.building.location?.locationFloorMapUrl
          ? buildingFormValues.position
          : this.commonUtils.normalizeLatLng(buildingFormValues.position), // if building has location floormap url, this means position are not lat, lng but x and y on the floor map
      };
      return building;
    } else {
      return null;
    }
  }

  /**
   * Called when the user selects a new floor map to upload.
   * @param event The event that triggered the function call, containing the selected file.
   * @returns Nothing.
   */
  public onFileSelect(event: FileSelectEvent) {
    const file = event.files[0];
    this.selectedFloorMap = file;
  }
  /**
   * Clears the selected floor map when the user clicks on the "Clear" button.
   * @param fileupload The FileUpload component that contains the selected file.
   */
  public onFileUploadClick(fileupload: FileUpload) {
    this.selectedFloorMap = null;
    fileupload.clear();
  }

  /**
   * Initializes the map and sets up the necessary configuration.
   */
  private initMap() {
    this.map = L.map("buildingMapContainer", environment.mapConfig).setView([51.1642292, 10.4541194], 6);
    const currentLang: SupportedLang = this.translate.currentLang as SupportedLang;

    const tilesUrl = environment.mapTileUrl[currentLang];
    if (tilesUrl) {
      this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);
      const provider = new OpenStreetMapProvider();
      const searchControl: L.Control = GeoSearchControl({
        provider: provider,
        style: "bar",
        searchLabel: this.translate.instant("common.enter_address"),
        updateMap: true,
        autoClose: true,
      });
      this.icon = this.initIcon();
      this.initMarker();
      this.initClickMapEvent();

      this.map.addControl(searchControl);
    }
  }

  /**
   * Initializes a marker on the map with the given latitude and longitude.
   */
  private initMarker() {
    const position = this.building.position;
    if (position) {
      const lat = position.latitude;
      const lng = position.longitude;
      if (lat && lng) {
        this.marker = L.marker([lat, lng], {
          icon: this.icon,
        }).addTo(this.map);
      }
    }
  }
  /**
   * Initializes the click event on the map.
   */
  protected initClickMapEvent() {
    this.map.on("click", (e: L.LeafletMouseEvent) => {
      const lat = e.latlng.lat;
      const lng = e.latlng.lng;
      if (!this.marker) {
        this.marker = L.marker([lat, lng], {
          icon: this.icon,
        }).addTo(this.map);
      } else {
        const newLatLng = new L.LatLng(lat, lng);
        this.marker.setLatLng(newLatLng);
      }
      this.form?.controls["position"].patchValue({
        latitude: lat,
        longitude: lng,
      });
    });
  }

  /**
   * Initializes an icon for a map library.
   * @return {L.icon} The initialized icon.
   */
  private initIcon() {
    return L.icon({
      iconSize: [25, 34], // size of the icon
      iconAnchor: [12.5, 25],
      iconUrl: "assets/icons/map_marker_ok.svg",
      popupAnchor: [0, -30],
    });
  }

  /**
   * Closes the current dialog by invoking the close method on the dialog reference.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Adds a new building to the server and if a floor map was selected, uploads it and edits the location with the floor map url.
   * If no floor map was selected, it only adds the building.
   */
  private addBuilding() {
    const directParent = this.building.directParentId;
    const createBuilding$ = this.assetService
      .createAsset(ASSET_TYPES.BUILDING, this.building, directParent)
      .pipe(first());

    if (this.selectedFloorMap && this.selectedFloorMap !== null) {
      // If file selected following steps: create building, upload floor map, edit location with floor map url
      createBuilding$
        .pipe(
          concatMap((buildingId: string | null) => {
            if (!buildingId) return of(null);
            this.building.id = buildingId;
            return this.uploadFloorMap();
          }),
          concatMap((floorMapRes: Media[] | null) => {
            if (floorMapRes) {
              this.building.floorMapUrl = floorMapRes[0].url;
              return this.assetService.editAsset(ASSET_TYPES.BUILDING, this.building);
            } else {
              return of(null);
            }
          }),
          first()
        )
        .subscribe(this.afterServerResponse());
    } else {
      createBuilding$.subscribe(this.afterServerResponse());
    }
  }

  /**
   * Edits a building.
   * If a new floor map was selected, edits the existing file or uploads a new one.
   * @returns {Observable<Media[] | null>} The uploaded media or null if there is no selected file
   */
  private editBuilding() {
    const editAsset$ = this.assetService.editAsset(ASSET_TYPES.BUILDING, this.building).pipe(first());

    // File was selected by user:
    if (this.selectedFloorMap) {
      // File existed before edit:
      if (this.mediaFile && this.mediaFile.id) {
        const editFile$ = this.mediaService.editMedia(this.mediaFile.id, this.selectedFloorMap, "floorMap");
        editFile$
          .pipe(
            concatMap((editFileRes: Media) => {
              this.building.floorMapUrl = editFileRes.url;
              return this.assetService.editAsset(ASSET_TYPES.BUILDING, this.building);
            }),
            first()
          )
          .subscribe({
            next: () => {
              this.ref.close(true);
            },
          });
        // File didnt exist before:
      } else {
        const uploadFile$ = this.uploadFloorMap();
        uploadFile$
          .pipe(
            concatMap((uploadFileRes: Media[] | null) => {
              if (uploadFileRes && uploadFileRes[0].url) {
                this.building.floorMap = uploadFileRes[0].url;
              }
              return this.assetService.editAsset(ASSET_TYPES.BUILDING, this.building);
            }),
            first()
          )
          .subscribe(this.afterServerResponse());
      }
    } else {
      editAsset$.subscribe(this.afterServerResponse());
    }
  }

  /**
   * Fetches the mediafile for the floor map url if it exists
   * and assigns it to the mediaFile property
   */
  private checkMedia() {
    if (this.building.id && this.building.floorMapUrl) {
      this.mediaService
        .getMedia(ASSET_TYPES.BUILDING, this.building.id, "FLOOR_MAP")
        .pipe(first())
        .subscribe({
          next: (media: Media[]) => {
            if (media) {
              const matchingMediaFile = media.find((mediaFile: Media) => mediaFile.url === this.building.floorMapUrl);
              if (matchingMediaFile) {
                this.mediaFile = matchingMediaFile;
              }
            }
          },
        });
    }
  }

  /**
   * Return an object with next and error callbacks for afterServerResponse
   * @returns {{next: () => void, error: () => void}}
   */
  private afterServerResponse() {
    return {
      next: () => {
        this.requestStarted.set(false);
        if (this.actionType === "edit") {
          this.cache.delete(environment.baseUrl + `/asset/${this.building.id}?assetType=BUILDING`);
        }
        this.ref.close(true);
      },
      error: () => {
        this.requestStarted.set(false);
      },
    };
  }

  /**
   * Uploads the selected floor map to the server
   * @returns {Observable<Media[] | null>} The uploaded media or null if there is no selected file
   */
  private uploadFloorMap(): Observable<Media[] | null> {
    if (this.building.id) {
      return this.mediaService.uploadMedia(
        ASSET_TYPES.BUILDING,
        this.building.id,
        "FLOOR_MAP",
        this.selectedFloorMap,
        "floorMap"
      );
    } else {
      return of(null);
    }
  }

  /**
   * Changes the map tiles to match the specified language.
   * Removes the current tile layer and adds a new one based on the new language.
   * Invalidates the map size to ensure proper rendering after changing tiles.
   *
   * @param newLang The new language for which to update the map tiles.
   */
  private changeTiles(newLang: SupportedLang) {
    if (this.map && this.tileLayer) {
      this.map.removeLayer(this.tileLayer);
      const tilesUrl = environment.mapTileUrl[newLang];
      this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);
      setTimeout(() => {
        this.map?.invalidateSize();
      }, 50);
    }
  }
}
